<template>
    <SideOver
        v-if="showSwiper"
        @onClickEnter="onClickEnter"
        isLarge
        :title="title"
        :is-show="isShow"
        isMultiple
        @onClose="() => onClose()"
        :label="content"
        :isLoading="isLoading"
        :buttons="getButtons()"
    >
        <swiper
            v-if="showSwiper"
            :controller="{ control: swiper }"
            :simulateTouch="false"
            :modules="[Virtual, Controller]"
            @swiper="setSwiper"
            :slides-per-view="1"
            :space-between="10"
            :initialSlide="activeIndex && parseInt(activeIndex)"
        >
            <swiper-slide
                class="relative"
                v-for="(item, index) in currentFormList"
                :key="item.id"
            >
                <div
                    class="flex flex-1 flex-col p-6 space-y-4 h-full overflow-y-auto"
                >
                    <!-- Custom Summary step with progress tracking -->
                    <div v-if="item.name === 'Summary'" class="w-full">
                        <Text
                            :content="item.name"
                            size="lg"
                            weight="bold"
                            color="black"
                            align="left"
                            custom-class="leading-normal"
                        />
                        <Text
                            :content="processDescription(item.description)"
                            size="sm"
                            weight="medium"
                            color="gray-600"
                            align="left"
                            custom-class="leading-normal mt-2"
                        />
                        <div class="space-y-4 mt-6">
                            <div
                                v-for="(
                                    checklistItem, index
                                ) in getSummaryChecklistItems()"
                                :key="index"
                                class="flex items-center space-x-3 p-4 rounded-lg border transition-all duration-300"
                                :class="{
                                    'bg-green-50 border-green-200':
                                        checklistItem.completed,
                                    'bg-blue-50 border-blue-200':
                                        checklistItem.loading,
                                    'bg-gray-50 border-gray-200':
                                        !checklistItem.completed &&
                                        !checklistItem.loading,
                                }"
                            >
                                <div class="flex-shrink-0">
                                    <div
                                        v-if="checklistItem.loading"
                                        class="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
                                    ></div>
                                    <div
                                        v-else-if="checklistItem.completed"
                                        class="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center"
                                    >
                                        <svg
                                            class="w-4 h-4 text-white"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clip-rule="evenodd"
                                            ></path>
                                        </svg>
                                    </div>
                                    <div
                                        v-else
                                        class="w-6 h-6 border-2 border-gray-300 rounded-full"
                                    ></div>
                                </div>
                                <div class="flex-1">
                                    <Text
                                        :content="checklistItem.text"
                                        size="sm"
                                        weight="medium"
                                        :color="
                                            checklistItem.completed
                                                ? 'green-700'
                                                : checklistItem.loading
                                                ? 'blue-700'
                                                : 'gray-700'
                                        "
                                    />
                                    <Text
                                        v-if="checklistItem.loading"
                                        content="Loading..."
                                        size="xs"
                                        color="blue-600"
                                        custom-class="mt-1"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Regular SurveyForm for other steps -->
                    <SurveyForm
                        v-else
                        :id="item.id"
                        :ref="(el) => assignRef(el, index)"
                        :list="item.list"
                        :title="item.name"
                        :index="activeIndex"
                        :description="processDescription(item.description)"
                        class="w-full"
                        titleTextSize="lg"
                        titleTextColor="black"
                        containerSpacing="space-y-3"
                        bodySpacing="space-y-3"
                        fieldSpacingClass="pt-4"
                        :showMandatoryLabel="false"
                        :modelKey="modelKey"
                        :orig-form-data="savedFormData"
                        @on-validate="onValidate"
                        :formData="finalData"
                    />
                </div>
            </swiper-slide>
        </swiper>
    </SideOver>
</template>

<script>
import Text from "../../atoms/Text/Text.vue";
import Icon from "../../atoms/Icons/Icons.vue";
import Input from "../../molecules/Inputs/Component/Component.vue";
import Textarea from "../../molecules/Textareas/Simple/Simple";
import Select from "../../molecules/Selects/Component/Component.vue";
import Button from "../../atoms/Button/Button.vue";
import SideOver from "../../organisms/SideOvers/WithBackgroundOverlay/WithBackgroundOverlay.vue";
import AutoComplete from "../../molecules/AutoComplete/AutoComplete";
import MultiSelectDropdown from "../../molecules/Dropdown/MultiSelectDropdown/MultiSelectDropdown";
import Simple from "../../organisms/Tables/Simple/Simple.vue";
// Vue imports removed since we're using Options API
import { Swiper, SwiperSlide } from "swiper/vue/swiper-vue";
import { Controller, Virtual } from "swiper";
import "swiper/swiper.min.css";
import SurveyForm from "../../organisms/SurveyForm/SurveyForm.vue";
import { upperFirst, get } from "lodash";
// Store will be available via this.actions and this.state in the component

export default {
    components: {
        Text,
        Icon,
        Input,
        Select,
        Button,
        SideOver,
        Textarea,
        Swiper,
        SwiperSlide,
        SurveyForm,
        AutoComplete,
        MultiSelectDropdown,
        Simple,
    },
    props: {
        isShow: {
            type: Boolean,
            default: true,
        },
        title: {
            type: String,
            default: "",
        },
        content: {
            type: String,
            default: "",
        },
        isLoading: {
            type: Boolean,
            default: true,
        },
        isSaving: {
            type: Boolean,
            default: false,
        },
        formList: {
            type: Array,
            default: () => [],
        },
        modelKey: {
            type: String,
            default: "id",
        },
        finalData: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            swiper: null,
            formRefs: [],
            surveyAnswersMap: {},
            savedFormData: {},
            isValid: false,
            activeIndex: 0,
            showSwiper: false,
            show: false,
            get,
            upperFirst,
            Controller,
            Virtual,
            targetCustomerNames: [], // Store customer names from Lambda
            dynamicFormList: [], // Store dynamic form configuration
            isLoadingCustomerNames: false, // Loading state for AWS call

            // Progress tracking for Lambda calls
            lambdaProgress: {
                targetCustomerDemographics: {
                    completed: false,
                    loading: false,
                },
                targetCustomerMarket: { completed: false, loading: false },
                targetCustomerOutreachAreas: {
                    completed: false,
                    loading: false,
                },
                opportunityMapping: { completed: false, loading: false },
            },
            allLambdaCallsComplete: false,
        };
    },
    computed: {
        currentFormList() {
            const result =
                this.dynamicFormList.length > 0
                    ? this.dynamicFormList
                    : this.formList;
            console.log(
                "🔄 currentFormList computed - dynamicFormList length:",
                this.dynamicFormList.length
            );
            console.log(
                "🔄 currentFormList computed - using:",
                this.dynamicFormList.length > 0 ? "dynamic" : "original"
            );
            console.log("🔄 currentFormList computed - result:", result);
            return result;
        },
        isAllLambdaCallsComplete() {
            const allComplete = Object.values(this.lambdaProgress).every(
                (call) => call.completed
            );
            console.log(
                "✅ Lambda calls complete check:",
                allComplete,
                this.lambdaProgress
            );
            return allComplete;
        },
    },
    watch: {
        isShow: function (val) {
            if (val) {
                this.$nextTick(() => {
                    setTimeout(() => (this.showSwiper = true), 50);
                });
                // Reset dynamic form data when modal opens
                this.targetCustomerNames = [];
                this.dynamicFormList = [];
            } else {
                setTimeout(() => (this.showSwiper = false), 700);
            }
        },
        savedFormData: {
            handler(newVal) {
                console.log("📊 savedFormData updated:", newVal);
            },
            deep: true,
        },
        activeIndex: function (newIndex) {
            console.log("🔄 Active index changed to:", newIndex);
            console.log("📝 Current savedFormData:", this.savedFormData);
        },
    },
    methods: {
        setSwiper(e) {
            if (e) {
                this.swiper = e;
            }
        },
        onClose() {
            // Clear Lambda data when closing the modal
            this.actions.lambda.clearLambdaData();
            this.$emit("onClose");
            setTimeout(() => (this.showSwiper = false), 500);
        },
        async onNext() {
            console.log(
                "🚀 onNext called - activeIndex:",
                this.activeIndex,
                "formRefs length:",
                this.formRefs.length
            );
            console.log(
                "🚀 formRefs[activeIndex]:",
                this.formRefs[this.activeIndex]
            );
            console.log("🚀 onNext call stack:", new Error().stack);

            // Only get form object if the form reference exists
            if (this.formRefs[this.activeIndex]) {
                this.surveyAnswersMap =
                    this.formRefs[this.activeIndex].getFormObject();
                this.savedFormData = {
                    ...this.savedFormData,
                    ...this.surveyAnswersMap,
                };
            } else {
                console.warn(
                    "⚠️ No form reference found for activeIndex:",
                    this.activeIndex
                );
            }

            // Debug: Log the saved form data
            console.log(
                "📝 Saved form data after step",
                this.activeIndex + 1,
                ":",
                this.savedFormData
            );

            // If this is the first step, get customer names before advancing
            if (this.activeIndex === 0) {
                console.log(
                    "🚀 First step completed - getting target customer names..."
                );
                console.log(
                    "🚀 Current savedFormData before Lambda call:",
                    this.savedFormData
                );

                // Set loading state
                this.isLoadingCustomerNames = true;

                try {
                    await this.getTargetCustomerNames();
                    console.log(
                        "🚀 After getTargetCustomerNames, targetCustomerNames:",
                        this.targetCustomerNames
                    );
                    console.log(
                        "🚀 After getTargetCustomerNames, dynamicFormList:",
                        this.dynamicFormList
                    );

                    // Only advance to next step after getting customer names
                    if (this.swiper) {
                        this.swiper.slideTo(this.activeIndex + 1);
                    }
                    this.activeIndex = this.activeIndex + 1;

                    if (this.activeIndex < this.currentFormList.length) {
                        let valid =
                            this.formRefs[this.activeIndex].checkValidForm();
                        this.onValidate(valid);
                    }
                } catch (error) {
                    console.error("❌ Error getting customer names:", error);
                } finally {
                    this.isLoadingCustomerNames = false;
                }
            } else {
                // For other steps, advance normally
                if (this.swiper) {
                    this.swiper.slideTo(this.activeIndex + 1);
                }
                this.activeIndex = this.activeIndex + 1;

                // Trigger other Lambda calls when reaching the Summary step
                const currentStep = this.currentFormList[this.activeIndex];
                if (currentStep && currentStep.name === "Summary") {
                    console.log(
                        "🚀 Summary step loaded - starting AWS Lambda calls..."
                    );
                    console.log(
                        "🚀 activeIndex:",
                        this.activeIndex,
                        "currentFormList.length:",
                        this.currentFormList.length
                    );
                    this.triggerLambdaCalls();
                    // Note: Not calling onSave() here - will wait for all Lambda calls to complete
                }

                if (
                    this.activeIndex < this.currentFormList.length &&
                    this.formRefs[this.activeIndex]
                ) {
                    let valid =
                        this.formRefs[this.activeIndex].checkValidForm();
                    this.onValidate(valid);
                }
            }
        },
        onBack() {
            if (
                this.activeIndex < this.formRefs?.length &&
                this.formRefs[this.activeIndex]
            ) {
                this.surveyAnswersMap =
                    this.formRefs[this.activeIndex].getFormObject();
                this.savedFormData = {
                    ...this.savedFormData,
                    ...this.surveyAnswersMap,
                };
            }

            if (this.swiper) {
                this.swiper.slideTo(this.activeIndex - 1);
            }
            this.activeIndex = this.activeIndex - 1;

            if (this.formRefs[this.activeIndex]) {
                let valid = this.formRefs[this.activeIndex].checkValidForm();
                this.onValidate(valid);
            }
        },
        activeBtnText() {
            if (this.isLoadingCustomerNames) {
                return "Loading...";
            } else {
                const currentStep = this.currentFormList[this.activeIndex];
                const isSummaryStep =
                    currentStep && currentStep.name === "Summary";

                if (isSummaryStep) {
                    // Summary step - show "Next" when all Lambda calls complete, "Loading..." otherwise
                    return this.isAllLambdaCallsComplete
                        ? "Next"
                        : "Loading...";
                } else if (
                    this.activeIndex ===
                    this.currentFormList.length - 1
                ) {
                    return "Save";
                } else {
                    return "Next";
                }
            }
        },
        getButtons() {
            const currentStep = this.currentFormList[this.activeIndex];
            const isSummaryStep = currentStep && currentStep.name === "Summary";

            console.log(
                "🔘 getButtons called - activeIndex:",
                this.activeIndex,
                "currentFormList.length:",
                this.currentFormList.length
            );
            console.log("🔘 Current step:", currentStep);
            console.log("🔘 Is Summary step?", isSummaryStep);

            if (this.activeIndex === 0) {
                return [
                    {
                        label: this.isLoadingCustomerNames
                            ? "Loading..."
                            : "Next",
                        type: "primary",
                        isLoading: this.isLoadingCustomerNames,
                        isDisabled:
                            this.formRefs[this.activeIndex]?.checkValidForm() ||
                            false ||
                            this.isLoadingCustomerNames,
                        action: () => this.onNext(),
                    },
                ];
            } else if (isSummaryStep) {
                // Summary step - show loading button while AWS calls are in progress, Next button when complete
                console.log(
                    "🔘 Returning Summary step buttons - all complete:",
                    this.isAllLambdaCallsComplete
                );
                const buttons = [];

                if (this.isAllLambdaCallsComplete) {
                    // All Lambda calls complete - show Next button
                    buttons.push({
                        label: "Next",
                        type: "primary",
                        isLoading: false,
                        isDisabled: false,
                        action: () => this.onNext(),
                    });
                } else {
                    // Lambda calls still in progress - show loading button
                    buttons.push({
                        label: "Loading...",
                        type: "primary",
                        isLoading: true,
                        isDisabled: true,
                        action: () => {}, // No action while loading
                    });
                }

                return buttons;
            } else {
                console.log("🔘 Returning regular step buttons (Back + Next)");
                return [
                    {
                        label: "Back",
                        type: "secondary",
                        isLoading: false,
                        action: () => this.onBack(),
                    },
                    {
                        label: this.activeBtnText(),
                        type: "primary",
                        isLoading: this.isSaving,
                        isDisabled:
                            this.formRefs[this.activeIndex]?.checkValidForm() ||
                            false,
                        action: () => this.onNext(),
                    },
                ];
            }
        },
        async getTargetCustomerNames() {
            console.log("🚀 Getting target customer names...");
            console.log("📝 Current savedFormData:", this.savedFormData);
            console.log("📝 Description:", this.savedFormData.description);

            // Ensure targetCustomerNames is initialized
            if (!this.targetCustomerNames) {
                this.targetCustomerNames = [];
                console.log(
                    "🔄 Initialized targetCustomerNames to empty array"
                );
            }

            try {
                const response = await this.actions.lambda.callLambdaBySlug({
                    slug: "targetCustomerNames",
                    parameters: { input_text: this.savedFormData.description },
                });
                console.log(
                    "✅ Lambda call successful for targetCustomerNames"
                );
                console.log("Lambda response data:", response);
                console.log("Response type:", typeof response);
                console.log("Is array:", Array.isArray(response));

                // Handle new JSON format with venture_names and target_customer_names
                if (response && typeof response === "object") {
                    // New format: { venture_names: [...], target_customer_names: [...] }
                    if (
                        response.venture_names &&
                        response.target_customer_names
                    ) {
                        console.log("📝 New JSON format detected");
                        console.log(
                            "📝 Venture names:",
                            response.venture_names
                        );
                        console.log(
                            "📝 Target customer names:",
                            response.target_customer_names
                        );

                        // Store venture names in savedFormData
                        this.savedFormData.venture_names =
                            response.venture_names;

                        // Store target customer names
                        this.targetCustomerNames =
                            response.target_customer_names;

                        console.log(
                            "📝 Stored venture_names:",
                            this.savedFormData.venture_names
                        );
                        console.log(
                            "📝 Stored target customer names:",
                            this.targetCustomerNames
                        );

                        // Create dynamic form list with Problem steps for each customer
                        this.createDynamicFormList();
                    } else {
                        console.warn(
                            "⚠️ Invalid JSON format - missing venture_names or target_customer_names"
                        );
                        console.warn("⚠️ Response:", response);
                        this.useFallbackData();
                    }
                } else if (Array.isArray(response) && response.length > 0) {
                    // Old format: array of customer names
                    console.log("📝 Old array format detected");
                    this.targetCustomerNames = response;
                    console.log(
                        "📝 Stored target customer names:",
                        this.targetCustomerNames
                    );

                    // Create dynamic form list with Problem steps for each customer
                    this.createDynamicFormList();
                } else {
                    console.warn(
                        "⚠️ No customer names returned from targetCustomerNames"
                    );
                    console.warn("⚠️ Response was not valid:", response);
                    this.useFallbackData();
                }
            } catch (error) {
                console.error(
                    "❌ Lambda call failed for targetCustomerNames:",
                    error
                );
                console.log("🔄 Using fallback data due to error...");
                this.useFallbackData();
            } finally {
                this.isLoadingCustomerNames = false;
            }
        },
        useFallbackData() {
            console.log("🔄 Using fallback data for testing...");
            this.targetCustomerNames = ["Drivers", "Riders"];
            this.savedFormData.venture_names = [
                "Ride Sharing App",
                "Transportation Service",
            ];
            this.createDynamicFormList();
        },
        createDynamicFormList() {
            console.log("🔄 createDynamicFormList called");
            console.log(
                "🔄 this.targetCustomerNames:",
                this.targetCustomerNames
            );
            console.log(
                "🔄 typeof this.targetCustomerNames:",
                typeof this.targetCustomerNames
            );
            console.log(
                "🔄 Array.isArray(this.targetCustomerNames):",
                Array.isArray(this.targetCustomerNames)
            );

            // Start with the original form list
            this.dynamicFormList = [...this.formList];

            // Check if targetCustomerNames exists and is an array
            if (
                !this.targetCustomerNames ||
                !Array.isArray(this.targetCustomerNames)
            ) {
                console.warn(
                    "⚠️ targetCustomerNames is not available or not an array:",
                    this.targetCustomerNames
                );
                console.warn("⚠️ Skipping dynamic form creation");
                return;
            }

            console.log(
                "🔄 About to iterate over targetCustomerNames:",
                this.targetCustomerNames
            );

            try {
                // Replace the original Problem step (index 1) with dynamic Problem steps for each customer
                // First, remove the original Problem step
                this.dynamicFormList.splice(1, 1);

                // Also remove the Summary step (now at index 1) and store it for later
                const summaryStep = this.dynamicFormList.splice(1, 1)[0];

                // Then insert dynamic Problem steps for each customer name
                this.targetCustomerNames.forEach((customerName, index) => {
                    const problemStep = {
                        id: `problem_${index + 2}`, // Start from 2 to avoid conflicts
                        name: "Problem",
                        description: `What do <strong>${customerName}</strong> need your help with?`,
                        list: [
                            {
                                id: `${index + 2}1`,
                                fields: [
                                    {
                                        id: `${index + 2}11`,
                                        modelField: "reasonFirst",
                                        addon: "They",
                                        inputType: "DROPDOWN_WITH_ADDON",
                                        placeholder: "i.e. need to, want to",
                                        options:
                                            '[{"id": 1, "name": "desperately need to", "value": "DESPERATELY_NEED_TO"},{"id": 2, "name": "have to", "value": "HAVE_TO"},{"id": 3, "name": "need to", "value": "NEED_TO"},{"id": 4, "name": "want to", "value": "WANT_TO"},{"id": 5, "name": "should", "value": "SHOULD"},{"id": 6, "name": "don\'t want to", "value": "DONT_WANT_TO"},{"id": 7, "name": "won\'t", "value": "WONT"}]',
                                    },
                                ],
                            },
                            {
                                id: `${index + 2}2`,
                                fields: [
                                    {
                                        id: `${index + 2}21`,
                                        modelField: "reasonSecond",
                                        placeholder:
                                            "i.e. go to the grocery store OR manage their money",
                                        inputType: "AUTOCOMPLETE",
                                        options: "[]",
                                        isNotMark: true,
                                        isRequired: true,
                                    },
                                ],
                            },
                            {
                                id: `${index + 2}3`,
                                fields: [
                                    {
                                        id: `${index + 2}31`,
                                        modelField: "reasonThird",
                                        addon: "but",
                                        inputType: "TAGS_WITH_ADDON",
                                        options: JSON.stringify([
                                            {
                                                id: 1,
                                                name: "it takes too long",
                                                label: "it takes too long",
                                            },
                                            {
                                                id: 2,
                                                name: "it costs too much",
                                                label: "it costs too much",
                                            },
                                            {
                                                id: 3,
                                                name: "it's too complicated",
                                                label: "it's too complicated",
                                            },
                                            {
                                                id: 4,
                                                name: "it's not reliable",
                                                label: "it's not reliable",
                                            },
                                            {
                                                id: 5,
                                                name: "it's not available when needed",
                                                label: "it's not available when needed",
                                            },
                                            {
                                                id: 6,
                                                name: "it doesn't work well",
                                                label: "it doesn't work well",
                                            },
                                            {
                                                id: 7,
                                                name: "it's too slow",
                                                label: "it's too slow",
                                            },
                                            {
                                                id: 8,
                                                name: "it's too expensive",
                                                label: "it's too expensive",
                                            },
                                            {
                                                id: 9,
                                                name: "it's hard to find",
                                                label: "it's hard to find",
                                            },
                                            {
                                                id: 10,
                                                name: "it's not convenient",
                                                label: "it's not convenient",
                                            },
                                        ]),
                                        isObject: true,
                                        placeholder:
                                            "i.e. it takes too long, it cost too much",
                                        isNotAdd: false,
                                    },
                                ],
                            },
                            {
                                id: `${index + 2}4`,
                                fields: [
                                    {
                                        id: `${index + 2}41`,
                                        addon: "because",
                                        modelField: "reasonFour",
                                        placeholder:
                                            "i.e. there aren't any stores near by",
                                        inputType: "TEXT_WITH_ADDON",
                                    },
                                ],
                            },
                        ],
                        modelForBox: {
                            content: "sample text with binded fields",
                        },
                    };

                    // Insert at index 1 (replacing the original Problem step)
                    this.dynamicFormList.splice(1 + index, 0, problemStep);
                });

                // Add the Summary step at the end
                this.dynamicFormList.push(summaryStep);
            } catch (error) {
                console.error("❌ Error in forEach loop:", error);
                console.error(
                    "❌ targetCustomerNames at error:",
                    this.targetCustomerNames
                );
                return;
            }

            console.log("📋 Created dynamic form list:", this.dynamicFormList);
            console.log("📋 Original form list:", this.formList);
            console.log(
                "📋 Current form list being used:",
                this.dynamicFormList.length > 0
                    ? this.dynamicFormList
                    : this.formList
            );
            console.log(
                "📋 Number of steps in dynamic list:",
                this.dynamicFormList.length
            );
            console.log(
                "📋 Number of steps in original list:",
                this.formList.length
            );

            // Force Vue to re-render the component
            this.$forceUpdate();

            // Also trigger a nextTick to ensure DOM updates
            this.$nextTick(() => {
                console.log("🔄 DOM updated after dynamic form list creation");
            });
        },
        async triggerLambdaCalls() {
            console.log(
                "🚀 Starting AWS Lambda calls for customer analysis..."
            );

            // Reset progress tracking
            this.lambdaProgress = {
                targetCustomerDemographics: { completed: false, loading: true },
                targetCustomerMarket: { completed: false, loading: true },
                targetCustomerOutreachAreas: {
                    completed: false,
                    loading: true,
                },
                opportunityMapping: { completed: false, loading: true },
            };
            this.allLambdaCallsComplete = false;

            try {
                // Chain targetCustomerDemographics, targetCustomerMarket, targetCustomerOutreachAreas, and opportunityMapping calls for each result
                if (this.targetCustomerNames.length > 0) {
                    console.log(
                        "🔄 Calling analysis functions for each customer name..."
                    );

                    // Call all Lambda functions for each customer name with delays
                    const customerAnalysisPromises =
                        this.targetCustomerNames.map(
                            async (customerName, index) => {
                                try {
                                    console.log(
                                        `Analyzing customer: "${customerName}" (${
                                            index + 1
                                        }/${this.targetCustomerNames.length})`
                                    );
                                    console.log(`Parameters being sent:`, {
                                        target_customer: customerName,
                                    });

                                    // Add delay before each customer analysis to avoid Bedrock throttling
                                    if (index > 0) {
                                        console.log(
                                            `⏳ Waiting 15 seconds before analyzing "${customerName}" to avoid Bedrock throttling...`
                                        );
                                        await new Promise((resolve) =>
                                            setTimeout(resolve, 15000)
                                        );
                                    }

                                    // Call Lambda functions sequentially with longer delays to avoid rate limiting
                                    console.log(
                                        `📊 Calling demographics for "${customerName}"...`
                                    );
                                    const demographicsResponse =
                                        await this.actions.lambda
                                            .callLambdaBySlug({
                                                slug: "targetCustomerDemographics",
                                                parameters: {
                                                    target_customer:
                                                        customerName,
                                                },
                                            })
                                            .catch((error) => {
                                                console.error(
                                                    `❌ Demographics call failed for "${customerName}":`,
                                                    error
                                                );
                                                return {
                                                    error: error.message,
                                                    customerName,
                                                };
                                            });

                                    console.log(
                                        `📊 Demographics response for "${customerName}":`,
                                        demographicsResponse
                                    );

                                    // Update progress for demographics
                                    if (
                                        demographicsResponse &&
                                        !demographicsResponse.error
                                    ) {
                                        this.lambdaProgress.targetCustomerDemographics.completed = true;
                                        this.lambdaProgress.targetCustomerDemographics.loading = false;
                                        console.log(
                                            "✅ Demographics completed successfully"
                                        );
                                        this.updateSummaryChecklist();
                                    }

                                    // Wait 15 seconds before next call to avoid Bedrock throttling
                                    console.log(
                                        `⏳ Waiting 15 seconds before market analysis to avoid Bedrock throttling...`
                                    );
                                    await new Promise((resolve) =>
                                        setTimeout(resolve, 15000)
                                    );

                                    console.log(
                                        `📈 Calling market analysis for "${customerName}"...`
                                    );
                                    const marketResponse =
                                        await this.actions.lambda
                                            .callLambdaBySlug({
                                                slug: "targetCustomerMarket",
                                                parameters: {
                                                    target_customer:
                                                        customerName,
                                                },
                                            })
                                            .catch((error) => {
                                                console.error(
                                                    `❌ Market call failed for "${customerName}":`,
                                                    error
                                                );
                                                return {
                                                    error: error.message,
                                                    customerName,
                                                };
                                            });

                                    console.log(
                                        `📈 Market response for "${customerName}":`,
                                        marketResponse
                                    );

                                    // Update progress for market
                                    if (
                                        marketResponse &&
                                        !marketResponse.error
                                    ) {
                                        this.lambdaProgress.targetCustomerMarket.completed = true;
                                        this.lambdaProgress.targetCustomerMarket.loading = false;
                                        console.log(
                                            "✅ Market analysis completed successfully"
                                        );
                                        this.updateSummaryChecklist();
                                    }

                                    // Wait 15 seconds before next call to avoid Bedrock throttling
                                    console.log(
                                        `⏳ Waiting 15 seconds before outreach areas to avoid Bedrock throttling...`
                                    );
                                    await new Promise((resolve) =>
                                        setTimeout(resolve, 15000)
                                    );

                                    console.log(
                                        `🎯 Calling outreach areas for "${customerName}"...`
                                    );
                                    const outreachResponse =
                                        await this.actions.lambda
                                            .callLambdaBySlug({
                                                slug: "targetCustomerOutreachAreas",
                                                parameters: {
                                                    target_customer:
                                                        customerName,
                                                },
                                            })
                                            .catch((error) => {
                                                console.error(
                                                    `❌ Outreach call failed for "${customerName}":`,
                                                    error
                                                );
                                                return {
                                                    error: error.message,
                                                    customerName,
                                                };
                                            });

                                    console.log(
                                        `🎯 Outreach response for "${customerName}":`,
                                        outreachResponse
                                    );

                                    // Update progress for outreach areas
                                    if (
                                        outreachResponse &&
                                        !outreachResponse.error
                                    ) {
                                        this.lambdaProgress.targetCustomerOutreachAreas.completed = true;
                                        this.lambdaProgress.targetCustomerOutreachAreas.loading = false;
                                        console.log(
                                            "✅ Outreach areas completed successfully"
                                        );
                                        this.updateSummaryChecklist();
                                    }

                                    // Wait 15 seconds before opportunity mapping call
                                    console.log(
                                        `⏳ Waiting 15 seconds before opportunity mapping...`
                                    );
                                    await new Promise((resolve) =>
                                        setTimeout(resolve, 15000)
                                    );

                                    // Get the reasonSecond from the saved form data for this customer
                                    const customerProblemKey = `problem_${index}`;
                                    const reasonSecond =
                                        this.savedFormData[customerProblemKey]
                                            ?.reasonSecond || "finding help";

                                    console.log(
                                        `🔗 Calling opportunity mapping for "${customerName}" with action: "${reasonSecond}"...`
                                    );
                                    const opportunityResponse =
                                        await this.actions.lambda
                                            .callLambdaBySlug({
                                                slug: "opportunityMapping",
                                                parameters: {
                                                    target_customer:
                                                        customerName,
                                                    action: reasonSecond,
                                                },
                                            })
                                            .catch((error) => {
                                                console.error(
                                                    `❌ Opportunity mapping call failed for "${customerName}":`,
                                                    error
                                                );
                                                return {
                                                    error: error.message,
                                                    customerName,
                                                };
                                            });

                                    console.log(
                                        `🔗 Opportunity mapping response for "${customerName}":`,
                                        opportunityResponse
                                    );

                                    // Update progress for opportunity mapping
                                    if (
                                        opportunityResponse &&
                                        !opportunityResponse.error
                                    ) {
                                        this.lambdaProgress.opportunityMapping.completed = true;
                                        this.lambdaProgress.opportunityMapping.loading = false;
                                        console.log(
                                            "✅ Opportunity mapping completed successfully"
                                        );
                                        this.updateSummaryChecklist();
                                    }

                                    console.log(
                                        `✅ Demographics for "${customerName}":`,
                                        demographicsResponse
                                    );
                                    console.log(
                                        `✅ Market for "${customerName}":`,
                                        marketResponse
                                    );
                                    console.log(
                                        `✅ Outreach areas for "${customerName}":`,
                                        outreachResponse
                                    );
                                    console.log(
                                        `✅ Opportunity mapping for "${customerName}":`,
                                        opportunityResponse
                                    );

                                    // Check if any responses failed and log warnings
                                    if (demographicsResponse?.error) {
                                        console.warn(
                                            `⚠️ Demographics failed for "${customerName}": ${demographicsResponse.error}`
                                        );
                                    } else if (
                                        !demographicsResponse ||
                                        demographicsResponse === null
                                    ) {
                                        console.warn(
                                            `⚠️ Demographics returned null/empty for "${customerName}"`
                                        );
                                    }

                                    if (marketResponse?.error) {
                                        console.warn(
                                            `⚠️ Market failed for "${customerName}": ${marketResponse.error}`
                                        );
                                    } else if (
                                        !marketResponse ||
                                        marketResponse === null
                                    ) {
                                        console.warn(
                                            `⚠️ Market returned null/empty for "${customerName}"`
                                        );
                                    }

                                    if (outreachResponse?.error) {
                                        console.warn(
                                            `⚠️ Outreach areas failed for "${customerName}": ${outreachResponse.error}`
                                        );
                                    } else if (
                                        !outreachResponse ||
                                        outreachResponse === null
                                    ) {
                                        console.warn(
                                            `⚠️ Outreach areas returned null/empty for "${customerName}"`
                                        );
                                    }

                                    if (opportunityResponse?.error) {
                                        console.warn(
                                            `⚠️ Opportunity mapping failed for "${customerName}": ${opportunityResponse.error}`
                                        );
                                    } else if (
                                        !opportunityResponse ||
                                        opportunityResponse === null
                                    ) {
                                        console.warn(
                                            `⚠️ Opportunity mapping returned null/empty for "${customerName}"`
                                        );
                                    }

                                    return {
                                        customerName,
                                        demographics: demographicsResponse,
                                        market: marketResponse?.error
                                            ? {
                                                  error: marketResponse.error,
                                                  fallback: {
                                                      marketSize:
                                                          "Data unavailable - market analysis failed",
                                                      competition:
                                                          "Unable to determine",
                                                      trends: "Analysis in progress",
                                                  },
                                              }
                                            : marketResponse,
                                        outreachAreas: outreachResponse,
                                        opportunityMapping: opportunityResponse,
                                    };
                                } catch (error) {
                                    console.error(
                                        `❌ Analysis failed for "${customerName}":`,
                                        error
                                    );
                                    return {
                                        customerName,
                                        demographics: null,
                                        market: null,
                                        outreachAreas: null,
                                        opportunityMapping: null,
                                        error: error.message,
                                    };
                                }
                            }
                        );

                    // Wait for all analysis calls to complete
                    const allAnalysisResults = await Promise.all(
                        customerAnalysisPromises
                    );
                    console.log(
                        "🎯 All customer analysis calls completed:",
                        allAnalysisResults
                    );

                    // Transform form data to targetCustomers array format
                    const targetCustomers =
                        this.transformFormDataToTargetCustomers();

                    // Helper function to convert object keys to lowercase first letter
                    const lowercaseFirstLetter = (obj) => {
                        if (!obj || typeof obj !== "object") return obj;
                        const newObj = {};
                        for (const [key, value] of Object.entries(obj)) {
                            const newKey =
                                key.charAt(0).toLowerCase() + key.slice(1);
                            newObj[newKey] = value;
                        }
                        return newObj;
                    };

                    // Integrate ventureData into llmData for each target customer
                    const transformedLlmData = allAnalysisResults.map(
                        (result) => {
                            // Find the corresponding target customer data
                            const customerData = targetCustomers.find(
                                (tc) => tc.name === result.customerName
                            );

                            return {
                                customerName: result.customerName,
                                demographics: lowercaseFirstLetter(
                                    result.demographics
                                ), // Object, not array
                                market: lowercaseFirstLetter(result.market), // Lowercase keys
                                outreachAreas: result.outreachAreas,
                                opportunityMapping: result.opportunityMapping,
                                // Add venture data for this specific customer
                                ventureData: {
                                    name:
                                        customerData?.name ||
                                        result.customerName,
                                    desireVerb: customerData?.desireVerb || "",
                                    desireReason:
                                        customerData?.desireReason || "",
                                    problemVerb:
                                        customerData?.problemVerb || [],
                                    problemReason:
                                        customerData?.problemReason || "",
                                },
                            };
                        }
                    );
                    console.log(
                        "🔄 Transformed LLM data with integrated venture data:",
                        transformedLlmData
                    );

                    // Prepare venture data with the new structure
                    const ventureNames = this.savedFormData.venture_names || [];
                    const randomVentureName =
                        ventureNames.length > 0
                            ? ventureNames[
                                  Math.floor(
                                      Math.random() * ventureNames.length
                                  )
                              ]
                            : "New Venture";

                    const ventureData = {
                        description: this.savedFormData.description,
                        name: randomVentureName,
                        summary: this.savedFormData.summary || [],
                        opportunities: [], // Add empty opportunities array to prevent backend error
                    };

                    // Call saveVentureWithAI mutation
                    console.log("🚀 Calling saveVentureWithAI mutation...");
                    console.log(
                        "📊 llmData (transformed):",
                        transformedLlmData
                    );
                    console.log("📊 ventureData (structured):", ventureData);

                    try {
                        const ventureResponse =
                            await this.actions.venture.saveVentureWithAI({
                                userId: this.state.currentUser.id,
                                llmData: transformedLlmData,
                                ventureData: ventureData,
                            });

                        console.log(
                            "✅ saveVentureWithAI mutation successful:",
                            ventureResponse
                        );

                        // Navigate to the first target customer detail page
                        const venture = ventureResponse.saveVentureWithAI;
                        const firstTargetCustomer =
                            venture.targetCustomerUsers?.[0];

                        if (firstTargetCustomer) {
                            this.$router.push({
                                name: "Venture Target Customer Detail",
                                params: {
                                    ventureId: venture.id,
                                    id: firstTargetCustomer.id,
                                },
                                query: {
                                    showOpportunityMap: true,
                                },
                            });
                        } else {
                            // Fallback to venture dashboard if no target customers
                            this.$router.push({
                                name: "Venture Dashboard",
                                params: {
                                    ventureId: venture.id,
                                },
                            });
                        }

                        // Close the modal
                        this.onClose();
                    } catch (error) {
                        console.error(
                            "❌ saveVentureWithAI mutation failed:",
                            error
                        );
                        // You might want to show an error message to the user here
                    }
                } else {
                    console.warn("⚠️ No customer names available for analysis");
                }
            } catch (error) {
                console.error("❌ Lambda calls failed:", error);

                if (error.message.includes("CORS")) {
                    console.error(
                        "CORS Error: AWS Lambda function needs CORS headers configured. Check console for details."
                    );
                } else {
                    console.error(`Lambda call failed: ${error.message}`);
                }
            }
        },
        getSummaryChecklistItems() {
            return [
                {
                    text: "Defining your target customers",
                    completed:
                        this.lambdaProgress.targetCustomerDemographics
                            .completed,
                    loading:
                        this.lambdaProgress.targetCustomerDemographics.loading,
                },
                {
                    text: "Determining your estimated market size",
                    completed:
                        this.lambdaProgress.targetCustomerMarket.completed,
                    loading: this.lambdaProgress.targetCustomerMarket.loading,
                },
                {
                    text: "Finding the best places to look for contacts",
                    completed:
                        this.lambdaProgress.targetCustomerOutreachAreas
                            .completed,
                    loading:
                        this.lambdaProgress.targetCustomerOutreachAreas.loading,
                },
                {
                    text: "Creating your opportunity map",
                    completed: this.lambdaProgress.opportunityMapping.completed,
                    loading: this.lambdaProgress.opportunityMapping.loading,
                },
            ];
        },
        updateSummaryChecklist() {
            // Force re-render of the summary checklist
            this.$forceUpdate();
            console.log(
                "📋 Updated summary checklist:",
                this.getSummaryChecklistItems()
            );
        },
        transformFormDataToTargetCustomers() {
            // Transform flat form data into structured targetCustomers array
            const targetCustomers = [];

            // Get the target customer names from the saved form data
            const customerNames = this.targetCustomerNames || [];

            customerNames.forEach((customerName, index) => {
                const customerData = {
                    name: customerName,
                    desireVerb:
                        this.savedFormData.reasonFirst?.value ||
                        this.savedFormData.reasonFirst?.name ||
                        this.savedFormData.reasonFirst,
                    desireReason:
                        this.savedFormData.reasonSecond?.label ||
                        this.savedFormData.reasonSecond?.name ||
                        this.savedFormData.reasonSecond,
                    problemVerb: this.savedFormData.reasonThird?.map(
                        (item) => item.name || item.label
                    ) || [this.savedFormData.reasonThird],
                    problemReason: this.savedFormData.reasonFour,
                };

                targetCustomers.push(customerData);
            });

            console.log("🔄 Transformed targetCustomers:", targetCustomers);
            console.log(
                "🔄 Saved form data keys:",
                Object.keys(this.savedFormData)
            );
            return targetCustomers;
        },
        async onSave() {
            const currentStep = this.currentFormList[this.activeIndex];
            const isSummaryStep = currentStep && currentStep.name === "Summary";

            console.log(
                "💾 onSave called - activeIndex:",
                this.activeIndex,
                "currentFormList.length:",
                this.currentFormList.length
            );
            console.log("💾 Current step:", currentStep);
            console.log("💾 Is Summary step?", isSummaryStep);
            console.log("💾 onSave call stack:", new Error().stack);
            console.log("💾 About to check if Summary step...");

            if (isSummaryStep) {
                // Summary step - DON'T emit saveForm event, just trigger Lambda calls
                console.log(
                    "🚀 Summary step - NOT emitting saveForm, just triggering Lambda calls..."
                );
                console.log("🚀 About to call triggerLambdaCalls...");
                this.triggerLambdaCalls();
                console.log("🚀 triggerLambdaCalls completed");
                console.log(
                    "🚀 Summary step - onSave completed without emitting saveForm"
                );
            } else {
                console.log(
                    "💾 Regular step - emitting saveForm and calling onNext..."
                );
                let formObject = this.formRefs[this.activeIndex]
                    ? this.formRefs[this.activeIndex].getFormObject()
                    : {};
                console.log(
                    "💾 About to emit saveForm with formObject:",
                    formObject,
                    "activeIndex:",
                    this.activeIndex
                );
                this.$emit("saveForm", formObject, this.activeIndex);
                console.log("💾 saveForm emitted, about to call onNext...");
                this.onNext();
                console.log("💾 onNext completed");
            }
        },
        assignRef(el, index) {
            console.log(
                "🔗 assignRef called - index:",
                index,
                "el:",
                el,
                "formRefs length:",
                this.formRefs.length
            );
            if (index >= this.formRefs.length) {
                this.formRefs.push(el);
            } else {
                this.formRefs[index] = el;
            }
            console.log("🔗 formRefs after assignment:", this.formRefs);
            return this.formRefs[index];
        },
        onValidate(val) {
            this.isValid = !val;
        },
        processDescription(description) {
            if (!description) return "";

            // Replace template variables with actual data
            let processedDescription = description;

            // Replace {description} with the actual description from savedFormData
            if (this.savedFormData.description) {
                // Make the description bold and lowercase
                const boldLowercaseDescription = `<strong>${this.savedFormData.description.toLowerCase()}</strong>`;
                processedDescription = processedDescription.replace(
                    /\{description\}/g,
                    boldLowercaseDescription
                );
            }

            console.log("🔄 Processing description:", {
                original: description,
                processed: processedDescription,
                savedFormData: this.savedFormData,
            });

            return processedDescription;
        },
        onClickEnter() {
            const currentStep = this.currentFormList[this.activeIndex];
            const isSummaryStep = currentStep && currentStep.name === "Summary";

            console.log(
                "⌨️ onClickEnter called - activeIndex:",
                this.activeIndex,
                "currentFormList.length:",
                this.currentFormList.length
            );
            console.log("⌨️ Current step:", currentStep);
            console.log("⌨️ Is Summary step?", isSummaryStep);
            console.log(
                "⌨️ Form valid?",
                this.formRefs?.[this.activeIndex]?.checkValidForm()
            );
            console.log("⌨️ onClickEnter call stack:", new Error().stack);

            if (
                this.formRefs?.[this.activeIndex] &&
                !this.formRefs[this.activeIndex].checkValidForm()
            ) {
                // Don't call onSave() on the Summary step - just trigger Lambda calls
                if (isSummaryStep) {
                    console.log(
                        "🚀 Summary step - triggering Lambda calls without saving..."
                    );
                    console.log(
                        "🚀 About to call triggerLambdaCalls from onClickEnter..."
                    );
                    this.triggerLambdaCalls();
                    console.log(
                        "🚀 triggerLambdaCalls completed from onClickEnter"
                    );
                } else {
                    console.log("⌨️ Regular step - calling onSave...");
                    console.log("⌨️ About to call onSave from onClickEnter...");
                    this.onSave();
                    console.log("⌨️ onSave completed from onClickEnter");
                }
            } else {
                console.log(
                    "⌨️ Form is valid or no formRef - calling onNext..."
                );
                this.onNext();
            }
        },
    },
    mounted() {
        // Clear Lambda data when starting a new idea
        this.actions.lambda.clearLambdaData();
        this.$nextTick(() => {
            this.show = this.isShow;
            // Ensure Swiper is initialized after DOM is ready
            setTimeout(() => {
                this.showSwiper = this.isShow;
            }, 100);
        });
    },
};
</script>

<style scoped>
.swiper {
    height: 100%;
}
</style>

<style>
.select-inline {
    display: flex;
    align-items: center;
}

.select-inline label {
    margin-bottom: 0 !important;
    margin-right: 20px;
    font-weight: 500 !important;
}

.select-inline div {
    flex: 1;
}
</style>
